import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { LetDirective } from '@ngrx/component';
import {
  cloneDeep,
  defaultsDeep,
  flatMap,
  forEach,
  get,
  keys,
  map,
} from 'lodash';
import { DynamicModule } from 'ng-dynamic-component';
import { map as rxMap, Observable, Subject, takeUntil } from 'rxjs';

import { MaterialModule } from '@lockton-apps/material';
import {
  expansion,
  MatTableAddonsDirective,
  UiModule,
} from '@lockton-apps/shared/ui/lui';

import {
  DYNAMIC_TABLE_CELLS,
  DynamicTableCells,
} from '../../constants/dynamic-table-cells.constant';
import {
  CustomTableEvent,
  DynamicTableBreakpoints,
  DynamicTableColumnConfig,
  DynamicTableColumnTypes,
  DynamicTableConfig,
} from '../../interfaces/dynamic-table.interface';
import { DynamicTableContextMenuComponent } from '../context-menu/dynamic-table-context-menu.component';
import { SelectionModel } from '@angular/cdk/collections';

@Component({
  selector: 'la-dynamic-table',
  imports: [
    CommonModule,
    MaterialModule,
    DynamicModule,
    LetDirective,
    MatTableAddonsDirective,
    UiModule,
    DynamicTableContextMenuComponent,
  ],
  templateUrl: './dynamic-table.component.html',
  styleUrls: ['./dynamic-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [expansion],
})
export class DynamicTableComponent<Entity>
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @ViewChild(MatSort, { static: false }) sort!: MatSort;
  @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;

  @Input() noDataMessage: string = $localize`No data to display`;
  @Input() additionalData: Record<string, unknown> | undefined = {};
  @Input() get data() {
    return this.dataSource.data;
  }
  set data(value: Entity[] | null | undefined) {
    if (!value) {
      return;
    }
    this.setDataSourceData(value);
  }
  @Input() get selection() {
    return this.selectionModel.selected;
  }
  set selection(value: Entity[]) {
    this.selectionModel.clear();
    this.selectionModel.select(...value);
  }
  @Input() separation: 'divider' | 'shaded' | 'alt' = 'shaded';
  @Input() isLoading: boolean = true;
  @Output() rowClicked: EventEmitter<Entity> = new EventEmitter();
  @Output() customEvent = new EventEmitter<CustomTableEvent<Entity>>();
  @Output() selectionChange = new EventEmitter<Entity[]>();
  @Output() expandedRowChange = new EventEmitter<Entity>();

  @Input() get config() {
    return this._config;
  }
  set config(value: DynamicTableConfig) {
    this._config = value;
    this.setColumns();
    this.initializeSelection();
    this.initializeExpansion();
    if (!this.sort) return;
    this.sort.sort({
      id: value.sort.active,
      start: value.sort.direction,
      disableClear: false,
    });
  }
  private _config!: DynamicTableConfig;

  readonly columnTypes: typeof DynamicTableColumnTypes =
    DynamicTableColumnTypes;

  breakpoint$!: Observable<DynamicTableBreakpoints>;
  dataSource = new MatTableDataSource<Entity>();
  activeRow!: number | string | undefined;
  activeRowContext!: number | string | undefined;
  expandedElement!: number | string | undefined;
  columns: DynamicTableColumnConfig[] = [];
  columnsToDisplay!: string[];
  selectionModel = new SelectionModel<Entity>(true, []);
  viewReady = false;

  private destroyed = new Subject<void>();

  get selectionCount(): number {
    return this.selectionModel.selected.length;
  }

  get isAllSelected() {
    const numSelected = this.selectionModel.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  constructor(
    private readonly breakpointObserver: BreakpointObserver,
    private readonly cdr: ChangeDetectorRef,
    @Inject(DYNAMIC_TABLE_CELLS)
    private columnTemplates: DynamicTableCells
  ) {}

  ngOnInit(): void {
    this.selectionModel.changed
      .pipe(takeUntil(this.destroyed))
      .subscribe(() => {
        this.selectionChange.emit(this.selectionModel.selected);
      });
  }

  ngOnChanges(): void {
    this.activeRowContext = undefined;
    this.activeRow = undefined;
  }

  ngAfterViewInit(): void {
    this.initializeOptionalFeatures();
    this.viewReady = true;
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  onRowClicked(row: Entity): void {
    this.rowClicked.emit(row);

    if (this.config.expansion?.expandOnRowClick) {
      this.toggleExpandedRow(row);
    }
  }

  toggleAllRowSelections(): void {
    if (this.isAllSelected) {
      this.selectionModel.clear();
      return;
    }

    this.selectionModel.select(...this.dataSource.data);
  }

  toggleExpandedRow(row: Entity): void {
    const selectedRow = this.getRowId(row);
    this.expandedElement =
      this.expandedElement === selectedRow ? undefined : selectedRow;
    this.expandedRowChange.emit(row);
    this.cdr.markForCheck();
  }

  getRowId(row: Entity): string | number {
    return get(row, this.config.idPath || 'id');
  }

  private setBreakpointObserver(columns: DynamicTableColumnConfig[]): void {
    this.breakpoint$ = this.breakpointObserver
      .observe(flatMap(map(columns, 'layout'), keys))
      .pipe(rxMap(this.getActiveBreakpoint.bind(this)));
  }

  private initializeOptionalFeatures(): void {
    this.initializeSorting();
    this.initializePagination();

    this.cdr.markForCheck();
  }

  private initializeSorting(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (data, sortHeaderId) => {
      const column = this.columns.find(({ id }) => id === sortHeaderId);
      return column?.props?.path
        ? get(data, column.props.path, '')
        : get(data, sortHeaderId);
    };
  }

  private initializePagination(): void {
    this.dataSource.paginator = this.paginator;
  }

  private setColumns(): void {
    const columnsClone = cloneDeep(this.config.columns);

    this.columns = map(columnsClone, (column) =>
      defaultsDeep(
        {
          template: get(this.columnTemplates, column.columnType || ''),
        },
        column
      )
    );

    this.columnsToDisplay = map(this.columns, 'id');

    this.setBreakpointObserver(this.columns);
  }

  private setDataSourceData(data: Entity[]): void {
    this.dataSource.data = data;
  }

  private getActiveBreakpoint({
    breakpoints,
  }: BreakpointState): DynamicTableBreakpoints {
    let biggestBreakpoint: DynamicTableBreakpoints =
      DynamicTableBreakpoints.DEFAULT;

    forEach(DynamicTableBreakpoints, (breakpoint): boolean | void => {
      if (breakpoints[breakpoint]) {
        biggestBreakpoint = breakpoint;
        return false;
      }
    });

    return biggestBreakpoint;
  }

  private initializeSelection(): void {
    if (this.config.selection?.on) {
      this.columnsToDisplay = ['select', ...this.columnsToDisplay];
    }
    if (this.config.selection?.compareWith) {
      this.selectionModel = new SelectionModel<Entity>(
        true,
        [],
        true,
        this.config.selection.compareWith
      );
    }
  }

  private initializeExpansion(): void {
    if (this.config.expansion?.column) {
      this.config.expansion.column.template = get(
        this.columnTemplates,
        this.config.expansion.column.columnType || ''
      );
    }
  }
}
